<script setup lang="ts">
import Navbar from "./components/layout/navbar_component.vue";
import Footer from "./components/layout/footer_component.vue";
import { useUserActivities } from "./store/userActivities";
import { useNotificationStore } from "./store/notification";
import { onMounted, watch } from "vue";
import { ref } from "vue";
import { useAuthStore } from "./store/auth";
import { API_BASE_URL, GETNOTIFICATION } from "./utils/appConst";
import "primeicons/primeicons.css";
import { NotificationStatus } from "./utils/notificationModel";
import { useI18n } from "vue-i18n";
const getNotificationUrl = `${API_BASE_URL}${GETNOTIFICATION}`;

const { locale } = useI18n();
const activitesStore = useUserActivities();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();
//const isMobileUser = ref(false);
const showCookiesAlert = ref(false);
const showNotificationReminder = ref(false);
/* onBeforeMount(() => {
  const userAgent = navigator.userAgent;
  isMobileUser.value = /android|iPad|iPhone|iPod/i.test(userAgent);
}); */

const setCookiesPermission = (result: boolean) => {
  authStore.setCookiesPermission(result);
  showCookiesAlert.value = false;
};

const dismissReminder = () => {
  showNotificationReminder.value = false;
  localStorage.setItem("lastNotificationReminder", new Date().toISOString());
};

const requestNotificationPermission = async () => {
  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      await startNotificationService();
      dismissReminder();
    } else {
      console.log("Notification permission denied.");
    }
  } else {
    dismissReminder();
  }
};

const startNotificationService = async () => {
  console.log("Initializing notification service...");
  navigator.serviceWorker.addEventListener("message", updateNotification);
  authStore.initFCMToken();
  if (authStore.token) {
    await authStore.validateToken(authStore.token);
  }
};

onMounted(async () => {
  document.addEventListener("click", closeLists);
  showCookiesAlert.value = authStore.showCookiesFlag!;
  const lastReminderDate = localStorage.getItem("lastNotificationReminder");
  const today = new Date().toISOString().split("T")[0];
  if (Notification.permission === "granted") {
    await startNotificationService();
  } else if (!lastReminderDate || !lastReminderDate.startsWith(today)) {
    showNotificationReminder.value = true;
  }
});

const updateNotification = async () => {
  try {
    const getData = await fetch(`${getNotificationUrl}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({ page: 1 }),
    });

    if (!getData.ok) {
      throw new Error(`HTTP error! Status: ${getData.status}`);
    }

    const { notifications } = await getData.json();

    notificationStore.dropNotificationList = notifications.map(
      (notification: any) => ({
        id: notification.notificationId,
        message: notification.message || null,
        type: notification.type,
        seen: notification.seen,
        createdAt: new Date(notification.createdDate),
        originalLang: notification.originalLang,
        targetLang: notification.targetLang,
        requestId: notification.requestId || null,
        relatedRequestId: notification.relatedRequestId || null,
        videoLink:
          notification.youtubeVideoId || notification.videoLink || null,
        audioLink: notification.audioLink || null,
        fileName: notification.videoTitle || notification.audioTitle || null,
        status:
          (notification.status as NotificationStatus) ||
          NotificationStatus.SUCCESS,
        uploadAlreadyUsed: notification.uploadAlreadyUsed === true,
        consumed: notification.consumed === true,
        orderSourceType: notification.orderSourceType ?? null,
      })
    );
    notificationStore.dropNotificationList.splice(6);
    authStore.getUnseenCount();
  } catch (error) {
    console.log("getNotification: ", error);
  }
};

const closeLists = () => {
  if (activitesStore.settingDropDown) {
    activitesStore.setDropDown(false);
  }
  if (activitesStore.localDropDown) {
    activitesStore.setLocalDropDown(false);
  }
  if (notificationStore.showNotificationPanel) {
    notificationStore.showNotificationPanel = false;
  }
};
</script>

<template>
  <div
    :class="{ 'font-arabica': locale === 'ar', 'font-sans': locale !== 'ar' }"
    class="flex flex-col min-h-screen"
  >
    <Navbar></Navbar>
    <!-- <div class="mx-auto" v-if="isMobileUser">
      <div
        class="w-full flex flex-col items-center justify-center top-28 bg-blue-500 text-white p-2 rounded-lg shadow-lg"
      >
        <p>{{ $t("for-a-better-experience-download-the-app") }}</p>
        <a href="" class="underline">{{ $t("download-now") }}</a>
      </div>
    </div> -->
    <div id="app" class="flex-grow flex flex-col">
      <router-view class="flex-grow" />
    </div>
    <Footer></Footer>
  </div>
  <div
    v-if="showCookiesAlert"
    class="fixed bottom-0 right-0 mb-4 mr-4 w-[70%] lg:w-[25%]"
  >
    <div
      class="bg-white rounded-lg shadow-xl py-4 px-6 border-2 border-slate-300"
    >
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center">
          <img
            src="https://www.svgrepo.com/show/401340/cookie.svg"
            alt="Cookie"
            class="h-8 w-8 mr-2"
          />
          <span class="text-gray-900 font-bold text-lg">{{
            $t("cookie-policy")
          }}</span>
        </div>
      </div>
      <p class="text-slate-700 text-lg font-semibold">
        {{ $t("We_use_cookies_to_enhance") + " " }}
        <a href="/cookies-policy">{{ $t("Learn_More") }}</a>
      </p>
      <div class="flex flex-row justify-between">
        <button
          @click="setCookiesPermission(true)"
          class="mt-4 bg-subDark text-white font-bold py-1 px-8 rounded"
        >
          {{ $t("accept-all-cookies") }}
        </button>
        <button
          @click="setCookiesPermission(false)"
          class="mt-4 border-[1px] border-subDark text-subDark font-bold py-1 px-8 rounded"
        >
          {{ $t("decline") }}
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="false"
    class="fixed bottom-10 left-4 bg-yellow-100 border border-yellow-500 text-yellow-800 px-4 py-3 rounded-lg shadow-lg w-[80%] lg:w-[25%] flex justify-between items-center"
  >
    <p class="text-sm">🔔 {{ $t("enable_notifications") }}</p>

    <div class="flex gap-2">
      <button
        @click="requestNotificationPermission"
        class="bg-yellow-700 text-white px-3 py-1 rounded-md text-sm font-medium hover:bg-yellow-600"
      >
        {{ $t("enable") }}
      </button>
      <button
        @click.stop="dismissReminder"
        class="text-yellow-700 font-bold text-lg"
      >
        ✖
      </button>
    </div>
  </div>
</template>
